import React, { useEffect, useState } from 'react'

import ImageGalleryModal from 'src/shared/features/users/components/public-profile/ui/image-gallery-modal'
import { cn } from 'src/shared/utils/cn'

interface ImageGridProps {
  images: string[]
  maxImages?: 2 | 5
  className?: string
  onImageClick?: (index: number) => void
  inverse?: boolean
}

type ImageCornerConfig = {
  tl?: boolean
  tr?: boolean
  bl?: boolean
  br?: boolean
}

const getCornerClasses = ({ tl, tr, bl, br }: ImageCornerConfig) =>
  cn(
    'rounded-[4px]',
    tl && 'rounded-tl-[15px]',
    tr && 'rounded-tr-[15px]',
    bl && 'rounded-bl-[15px]',
    br && 'rounded-br-[15px]',
  )

interface GridImageProps {
  src: string
  index: number
  onClick: (e: React.MouseEvent, index: number) => void
  className?: string
  style?: React.CSSProperties
  onLoad?: (event: React.SyntheticEvent<HTMLImageElement>) => void
}

const GridImage = ({ src, index, onClick, className, style, onLoad }: GridImageProps) => {
  return (
    <img
      src={src}
      alt={`Image ${index + 1}`}
      className={cn('size-full cursor-pointer border object-cover', className)}
      onClick={(e) => onClick(e, index)}
      style={style}
      onLoad={onLoad}
    />
  )
}

export const ImageGrid = ({ images, maxImages = 5, className, onImageClick, inverse }: ImageGridProps) => {
  const [imageDialogOpen, setImageDialogOpen] = useState(false)
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)
  const [singleAspectRatio, setSingleAspectRatio] = useState<number | null>(null)

  const handleImageClick = (e: React.MouseEvent, index: number) => {
    e.stopPropagation()
    setSelectedImageIndex(index)
    if (onImageClick) {
      onImageClick(index)
    } else {
      setImageDialogOpen(true)
    }
  }

  useEffect(() => {
    setSingleAspectRatio(null)
  }, [images.length])

  if (images.length === 0) return null

  const renderGrid = () => {
    switch (images.length) {
      case 1: {
        const isLandscape = singleAspectRatio ? singleAspectRatio > 1 : true
        const singleWidth =
          singleAspectRatio && singleAspectRatio <= 1 ? Math.round(500 * singleAspectRatio) : undefined

        const handleSingleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
          const { naturalWidth, naturalHeight } = event.currentTarget
          if (naturalWidth && naturalHeight) {
            setSingleAspectRatio(naturalWidth / naturalHeight)
          }
        }

        return (
          <div
            className={cn(
              'h-[500px] max-w-[700px] overflow-hidden rounded-[15px]',
              isLandscape ? 'w-full' : 'inline-flex',
              className,
            )}
            style={singleWidth ? { width: singleWidth } : undefined}
          >
            <GridImage
              src={images[0]}
              index={0}
              onClick={handleImageClick}
              onLoad={handleSingleImageLoad}
              className="size-full rounded-[15px]"
            />
          </div>
        )
      }
      case 2:
        return (
          <div
            className={cn(
              'flex h-[375px] w-full max-w-[700px] gap-[6px] overflow-hidden rounded-[15px]',
              inverse && 'flex-row-reverse',
              className,
            )}
          >
            <div className="flex-1">
              <GridImage
                src={images[0]}
                index={0}
                onClick={handleImageClick}
                className={getCornerClasses(inverse ? { tr: true, br: true } : { tl: true, bl: true })}
              />
            </div>
            <div className="flex-1">
              <GridImage
                src={images[1]}
                index={1}
                onClick={handleImageClick}
                className={getCornerClasses(inverse ? { tl: true, bl: true } : { tr: true, br: true })}
              />
            </div>
          </div>
        )
      case 3:
        return (
          maxImages === 5 && (
            <div
              className={cn(
                'flex h-[500px] w-full max-w-[700px] gap-[6px] overflow-hidden rounded-[15px]',
                inverse && 'flex-row-reverse',
                className,
              )}
            >
              <div className="flex-1">
                <GridImage
                  src={images[0]}
                  index={0}
                  onClick={handleImageClick}
                  className={getCornerClasses(inverse ? { tr: true, br: true } : { tl: true, bl: true })}
                />
              </div>
              <div className="flex flex-1 flex-col gap-[6px]">
                <div className="flex-1">
                  <GridImage
                    src={images[1]}
                    index={1}
                    onClick={handleImageClick}
                    className={getCornerClasses(inverse ? { tl: true } : { tr: true })}
                  />
                </div>
                <div className="flex-1">
                  <GridImage
                    src={images[2]}
                    index={2}
                    onClick={handleImageClick}
                    className={getCornerClasses(inverse ? { bl: true } : { br: true })}
                  />
                </div>
              </div>
            </div>
          )
        )
      case 4:
        return (
          maxImages === 5 && (
            <div
              className={cn(
                'flex h-[500px] w-full max-w-[700px] gap-[6px] overflow-hidden rounded-[15px]',
                inverse && 'flex-row-reverse',
                className,
              )}
            >
              <div className="flex flex-1 flex-col gap-[6px]">
                <div className="flex-1">
                  <GridImage
                    src={images[0]}
                    index={0}
                    onClick={handleImageClick}
                    className={getCornerClasses(inverse ? { tr: true } : { tl: true })}
                  />
                </div>
                <div className="flex-1">
                  <GridImage
                    src={images[1]}
                    index={1}
                    onClick={handleImageClick}
                    className={getCornerClasses(inverse ? { br: true } : { bl: true })}
                  />
                </div>
              </div>
              <div className="flex flex-1 flex-col gap-[6px]">
                <div className="flex-1">
                  <GridImage
                    src={images[2]}
                    index={2}
                    onClick={handleImageClick}
                    className={getCornerClasses(inverse ? { tl: true } : { tr: true })}
                  />
                </div>
                <div className="flex-1">
                  <GridImage
                    src={images[3]}
                    index={3}
                    onClick={handleImageClick}
                    className={getCornerClasses(inverse ? { bl: true } : { br: true })}
                  />
                </div>
              </div>
            </div>
          )
        )
      case 5:
        return (
          maxImages === 5 && (
            <div
              className={cn(
                'flex h-[500px] w-full max-w-[700px] flex-col gap-[6px] overflow-hidden rounded-[15px]',
                className,
              )}
            >
              <div className={cn('flex flex-1 gap-[6px]', inverse && 'flex-row-reverse')}>
                <div className="flex-1">
                  <GridImage
                    src={images[0]}
                    index={0}
                    onClick={handleImageClick}
                    className={getCornerClasses(inverse ? { tr: true } : { tl: true })}
                  />
                </div>
                <div className="flex-1">
                  <GridImage
                    src={images[1]}
                    index={1}
                    onClick={handleImageClick}
                    className={getCornerClasses(inverse ? { tl: true } : { tr: true })}
                  />
                </div>
              </div>
              <div className={cn('flex flex-1 gap-[6px]', inverse && 'flex-row-reverse')}>
                <div className="flex-1">
                  <GridImage
                    src={images[2]}
                    index={2}
                    onClick={handleImageClick}
                    className={getCornerClasses(inverse ? { br: true } : { bl: true })}
                  />
                </div>
                <div className="flex-1">
                  <GridImage src={images[3]} index={3} onClick={handleImageClick} className={getCornerClasses({})} />
                </div>
                <div className="flex-1">
                  <GridImage
                    src={images[4]}
                    index={4}
                    onClick={handleImageClick}
                    className={getCornerClasses(inverse ? { bl: true } : { br: true })}
                  />
                </div>
              </div>
            </div>
          )
        )
      default:
        return null
    }
  }

  return (
    <>
      {renderGrid()}

      {!onImageClick && (
        <ImageGalleryModal
          open={imageDialogOpen}
          onClose={() => setImageDialogOpen(false)}
          images={images}
          initialImageIndex={selectedImageIndex}
        />
      )}
    </>
  )
}
